import toast from 'core/toast'
import { AppDispatch } from 'state/store'
import { useNavigate } from 'react-router-dom'
import { focusFirstError } from 'core/helpers'
import { V1ApiUsersClient } from 'core/http-common'
import useQueryParam from 'core/hooks/useQueryParam'
import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { loginAction } from 'state/actions/userActions'
import ConfirmModal from 'components/Modals/ConfirmModal'
import { AlertMessage, IPageProps, IRootState } from 'types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DefaultButton, FormInput, MessageAlert } from 'components'
import { Field, FieldValidatorType, Form, FormClassComponent, FormElement } from '@progress/kendo-react-form'
import { faAngleDoubleRight, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons'
import { ForgotPasswordDto, ResetPasswordDto, LoginDto } from '@v1UsersApi'
import { matchValidator, minimumLengthValidator, RequiredValidator } from 'core/Validators'

interface IQueryParams {
    code?: string,
    returnPath?: string
}

interface IForgotPasswordFormState {
    userName: string
}

interface IResetPasswordFormState {
    password: string
    repeatPassword: string
}

interface ILoginFormState {
    userName: string
    password: string
}

const LoginPage = ({ title }: IPageProps) => {
    const { code, returnPath } = useQueryParam<IQueryParams>()

    const navigate = useNavigate()
    const dispatch = useDispatch<AppDispatch>()

    const hasNavigated = useRef<boolean>(false)

    const authErrorAlert = useSelector((state: IRootState) => ({ variant: 'danger', messages: state.user.authErrors } as AlertMessage))
    const loading = useSelector((state: IRootState) => state.app.loading)
    const authenticated = useSelector((state: IRootState) => state.user.authenticated)
    const multiFactorLoginEnabled = useSelector((state: IRootState) => state.user.multiFactorLoginEnabled)
    const homePage = useSelector((state: IRootState) => state.user.homePage)

    const [isVisible, setIsVisible] = useState(false)

    const [showResetPasswordModal, setShowResetPasswordModal] = useState(false)
    const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false)

    const [loadingForgotPassword, setLoadingForgotPassword] = useState(false)
    const [loadingResetPassword, setLoadingResetPassword] = useState(false)

    const formForgotPasswordRef = useRef<FormClassComponent>(null)
    const formResetPasswordRef = useRef<FormClassComponent>(null)

    const handleLoginOnSubmit = async (values: ILoginFormState) => {
        const loginDto = new LoginDto({
            userName: values.userName,
            password: values.password
        })
        await dispatch(loginAction(loginDto))
    }

    useEffect(() => {
        (async () => {
            if (authenticated && homePage && !hasNavigated.current) {
                if (multiFactorLoginEnabled) {
                    navigate(
                        process.env.REACT_APP_2FA_REDIRECT!,
                        {
                            state: {
                                redirectUrl: '/'
                            }
                        })
                }
                else {

                    if (returnPath) {
                        if (process.env.REACT_APP_ENABLE_OLD_SA_INTEGRATION === 'true' && returnPath.endsWith('aspx')) {
                            window.location.href = `${process.env.REACT_APP_OLD_SYS_ADMIN_URL}${decodeURI(returnPath)}`
                        }
                        else {
                            navigate(returnPath)
                        }
                    }
                    else if (homePage.startsWith('http')) {
                        window.location.href = homePage
                    }
                    else
                        navigate(homePage)
                }
                hasNavigated.current = true
            }
        })()
    }, [authenticated, multiFactorLoginEnabled, homePage, navigate])

    const [resetPasswordValidators, setResetPasswordValidators] = useState<FieldValidatorType | FieldValidatorType[]>()

    useEffect(() => {
        setResetPasswordValidators([
            () => matchValidator(formResetPasswordRef.current?.valueGetter('password'), formResetPasswordRef.current?.valueGetter('repeatPassword'), 'Passwords do not match'),
            () => minimumLengthValidator(formResetPasswordRef.current?.valueGetter('password'), 5),
            RequiredValidator
        ])
    }, [])

    const onForgotPassword = async (values: IForgotPasswordFormState) => {

        try {

            setLoadingForgotPassword(true)

            await V1ApiUsersClient.forgotPassword(new ForgotPasswordDto({ username: values.userName }))
            toast.success('If the username exists, a reset password email has been sent to the account on file.')

        } catch (e) {
            toast.responseErrors(e)
        }
        finally {
            setLoadingForgotPassword(false)
            setShowForgotPasswordModal(false)
        }
    }

    const onResetPassword = async (values: IResetPasswordFormState) => {
        if (!code)
            return
        try {

            setLoadingResetPassword(true)

            await V1ApiUsersClient.resetPassword(new ResetPasswordDto({
                resetCode: code,
                newPassword: values.password.trim(),
            }))
            toast.success('Reset password successfully')
            navigate('/login')
        } catch (e) {
            toast.responseErrors(e)
        }
        finally {
            setLoadingResetPassword(false)
            setShowResetPasswordModal(false)
        }
    }

    useEffect(() => {
        if (code) {
            setShowResetPasswordModal(true)
        }
    }, [code])

    return (
        <div id='LoginPage'>
            <div className='absCenter is-Responsive'>
                <div className='bg-light border rounded shadow-sm p-3'>
                    <h1 className='mb-3'><i><FontAwesomeIcon icon='folder-gear' /> System Admin</i></h1>
                    <hr />
                    <Form
                        onSubmit={(e) => handleLoginOnSubmit(e as ILoginFormState)}
                        render={(formRenderProps) => (
                            <FormElement>
                                <Field
                                    component={FormInput}
                                    data-cy='login-userName'
                                    name='userName'
                                    label='User Name:'
                                    required={true}
                                />

                                <Field
                                    component={FormInput}
                                    data-cy='login-password'
                                    name='password'
                                    label='Password:'
                                    type={isVisible ? 'text' : 'password'}
                                    required={true}
                                    inputSuffix={
                                        <>
                                            <FontAwesomeIcon icon={isVisible ? faEyeSlash : faEye} onClick={() => setIsVisible(!isVisible)} />
                                        </>
                                    }
                                />

                                <div className='mt-2'>
                                    <DefaultButton
                                        data-cy='login-submit'
                                        className='btn btn-primary w-100'
                                        type='submit'
                                        disabled={loading}
                                        loading={loading}
                                        onClick={() => focusFirstError(formRenderProps)}>
                                        LOGIN
                                    </DefaultButton>
                                </div>
                                <div className='d-flex justify-content-end'>
                                    <a
                                        data-cy='show-forgot-password-modal'
                                        className='text-primary text-decoration-none cursor-pointer pt-2'
                                        onClick={() => {
                                            setShowForgotPasswordModal(true)
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faAngleDoubleRight} /> Forgot Password?
                                    </a>
                                </div>

                                <MessageAlert
                                    data-cy='login-alert'
                                    alert={authErrorAlert}
                                />

                            </FormElement>
                        )} />
                </div>
            </div>

            <ConfirmModal
                data-cy='forgot-password-modal'
                show={showForgotPasswordModal}
                title='Reset Password'
                confirmButtonTitle='Reset Password'
                loading={loadingForgotPassword}
                disabled={loadingForgotPassword}
                onCancel={() => setShowForgotPasswordModal(false)}
                onSave={(e) => formForgotPasswordRef.current?.onSubmit(e)}
                content={
                    <Form
                        ref={formForgotPasswordRef}
                        ignoreModified={true}
                        onSubmit={(e) => onForgotPassword(e as IForgotPasswordFormState)}
                        render={() => (
                            <FormElement>
                                <p>
                                    <FontAwesomeIcon icon={'info-circle'} /> To begin the process of resetting your password, you'll need to enter your username below.
                                </p>
                                <Field
                                    component={FormInput}
                                    data-cy='forgot-password-modal-userName'
                                    name='userName'
                                    label='User Name:'
                                    disabled={loadingForgotPassword}
                                    required={true}
                                    validator={RequiredValidator}
                                />

                            </FormElement>
                        )}
                    />
                }
            />

            <ConfirmModal
                data-cy='reset-password-modal'
                show={showResetPasswordModal}
                title='Reset Password'
                confirmButtonTitle='Reset Password'
                loading={loadingResetPassword}
                disabled={loadingResetPassword}
                onCancel={() => setShowResetPasswordModal(false)}
                onSave={(e) => formResetPasswordRef.current?.onSubmit(e)}
                content={
                    <Form
                        ref={formResetPasswordRef}
                        ignoreModified={true}
                        onSubmit={(e) => onResetPassword(e as IResetPasswordFormState)}
                        render={() => (
                            <FormElement>
                                <p>
                                    <FontAwesomeIcon icon={'info-circle'} /> Enter your new password below to finish resetting your password.
                                </p>
                                <Field
                                    component={FormInput}
                                    data-cy='reset-password-modal-userName'
                                    name='password'
                                    label='Password:'
                                    type='password'
                                    disabled={loadingForgotPassword}
                                    required={true}
                                    validator={resetPasswordValidators}
                                />

                                <Field
                                    component={FormInput}
                                    data-cy='reset-password-modal-repeatPassword'
                                    name='repeatPassword'
                                    label='Repeat Password:'
                                    type='password'
                                    disabled={loadingForgotPassword}
                                    required={true}
                                    validator={resetPasswordValidators}
                                />

                            </FormElement>
                        )}
                    />
                }
            />
        </div>
    )
}

export default LoginPage
